import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Page } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ViewOnStorefrontButton } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { emptyPageForm, fromPage, toPageCreate, toPageUpdate } from '../api/mappers';
import { DeletePageDialog } from '../components/delete-page-dialog';
import { PageForm } from '../components/page-form';
import { PageStatusBadge } from '../components/page-status-badge';
import { usePage } from '../hooks/use-page';
import { useCreatePage, useUpdatePage } from '../hooks/use-page-mutations';
import { pagePath } from '../schemas/page-rules';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/content/pages/$pageId' as const;

function PageCreate() {
  const { t } = useTranslation('pages');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreatePage(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        backTo={{ to: '/s/$storeId/content/pages', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <PageForm
        storeId={store.id}
        page={null}
        defaultValues={emptyPageForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toPageCreate(values));
          await navigate({
            to: '/s/$storeId/content/pages/$pageId',
            params: { storeId: store.id, pageId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function DangerZone({ storeId, page }: { storeId: string; page: Page }) {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<Page | null>(null);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(page)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeletePageDialog
        storeId={storeId}
        page={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/content/pages', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function PageEdit() {
  const { t } = useTranslation('pages');
  const format = useFormat();
  const store = useStore();
  const { pageId } = useParams({ from: EDIT_ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: page } = usePage(store.id, pageId);
  const update = useUpdatePage(store.id);
  if (!page) return null;

  return (
    <PageContent>
      <PageHeader
        title={page.title}
        meta={<PageStatusBadge status={page.status} />}
        description={
          <span title={format.utc(page.updatedAt)}>
            <code>{pagePath(page.handle)}</code>
            {' · '}
            {t('updatedAt', { when: format.relative(page.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/content/pages', params: { storeId: store.id } }}
        backLabel={t('title')}
        actions={
          <ViewOnStorefrontButton url={page.storefrontUrl} live={page.status === 'published'} />
        }
      />
      <PageForm
        key={page.updatedAt}
        storeId={store.id}
        page={page}
        defaultValues={fromPage(page)}
        isSubmitting={update.isPending}
        onSubmit={(values) => update.mutateAsync({ id: page.id, body: toPageUpdate(values) })}
        dangerZone={
          <Can permission="content.write">
            <DangerZone storeId={store.id} page={page} />
          </Can>
        }
      />
    </PageContent>
  );
}

export function PageCreatePage() {
  return (
    <FeatureErrorBoundary name="pages.create">
      <PageCreate />
    </FeatureErrorBoundary>
  );
}

export function PageEditPage() {
  return (
    <FeatureErrorBoundary name="pages.edit">
      <PageEdit />
    </FeatureErrorBoundary>
  );
}
